import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms_accepted: false,
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { csrfToken, fetchCsrfToken, supabase, setIsRegistering } = useAuth();

  useEffect(() => {
    if (!csrfToken) {
      console.log('Register: Fetching CSRF token on mount');
      fetchCsrfToken();
    }
  }, [csrfToken, fetchCsrfToken]);

  useEffect(() => {
    document.title = 'Register | Scucci Systems';
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/signin-check`,
        },
      });

      if (signInError) {
        throw new Error(signInError.message);
      }
    } catch (err) {
      setError(err.message || 'Google Sign-In failed to initiate.');
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setIsRegistering(true);

    const businessName = formData.name.trim();
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const email = formData.email.trim();

    if (!businessName || !firstName || !lastName || !email) {
      setError('Please complete all required fields.');
      setLoading(false);
      setIsRegistering(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      setIsRegistering(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords must match.');
      setLoading(false);
      setIsRegistering(false);
      return;
    }

    if (!formData.terms_accepted) {
      setError('You must agree to the Terms & Privacy to register.');
      setLoading(false);
      setIsRegistering(false);
      return;
    }

    const signUpData = {
      email,
      password: formData.password,
      options: {
        data: {
          name: businessName,
          first_name: firstName,
          last_name: lastName,
          business_type: 'estate_sales',
          business_subtype: 'standard',
          credit_card_fee_pct: 0,
          cc_collection_enabled: false,
          phone: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          terms_accepted: formData.terms_accepted,
          accepted_at: new Date().toISOString(),
        },
        emailRedirectTo: `${window.location.origin}/signin`,
      },
    };

    console.log('Register: Supabase signUp data:', JSON.stringify(signUpData, null, 2));

    try {
      const { data, error: signUpError } = await supabase.auth.signUp(signUpData);

      if (signUpError) {
        if (signUpError.message.includes('duplicate') || signUpError.message.includes('already exists')) {
          setError('You are already registered. Redirecting to sign in...');
          setTimeout(() => navigate('/signin'), 3000);
        } else {
          throw signUpError;
        }
        return;
      }

      if (data.session) {
        navigate('/welcome');
      } else {
        setSubmittedEmail(email);
      }
    } catch (err) {
      setError(err.message || 'Unexpected error');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
      setIsRegistering(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="absolute top-4 right-4">
          <img src="/images/scucci-core-icon.png" alt="Scucci Systems Logo" className="h-10 w-10" />
        </div>
        <div className="flex-grow p-4 flex items-center justify-center">
          <div className="max-w-lg w-full bg-white p-8 rounded-lg shadow-md text-center">
            <h1 className="text-3xl font-bold mb-4 text-gray-800">Check your email</h1>
            <p className="text-lg text-gray-700 mb-4">We sent a confirmation link to:</p>
            <p className="text-xl font-semibold text-blue-600 mb-6 break-words">{submittedEmail}</p>
            <p className="text-gray-700 mb-6">
              Please open that email and click the confirmation link before signing in.
              If you do not see it, check your spam or junk folder.
            </p>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
            >
              Go to Sign In
            </button>
            <button
              type="button"
              onClick={() => setSubmittedEmail('')}
              className="w-full mt-4 text-blue-500 hover:underline"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="absolute top-3 right-3">
        <img src="/images/scucci-core-icon.png" alt="Scucci Systems Logo" className="h-8 w-8" />
      </div>
      <div className="flex-grow px-3 py-2">
        <div className="text-center mb-2">
          <h1 className="text-3xl font-bold mb-1 text-gray-800">Scucci Systems</h1>
          <h2 className="text-lg font-semibold text-gray-700">Estate Sale Business Platform</h2>
        </div>
        <div className="max-w-md mx-auto bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-3 text-gray-700">Create your account</h2>
          {error && <p className="text-sm text-red-500 mb-2">{error}</p>}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center py-1.5 px-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition duration-200 disabled:bg-gray-200 mb-3"
            disabled={loading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="mr-2">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            <span className="ml-2 text-sm text-gray-700 font-medium">Continue with Google</span>
          </button>
          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-500">OR Create Account with Email</span>
            </div>
          </div>
          <form onSubmit={handleRegister} className="space-y-2.5">
            <div>
              <label className="block mb-0.5 text-sm text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="block mb-0.5 text-sm text-gray-700">Create Password</label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block mb-0.5 text-sm text-gray-700">Confirm Password</label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <label className="flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="h-3.5 w-3.5 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2">Show passwords</span>
            </label>
            <div>
              <label className="block mb-0.5 text-sm text-gray-700">Business Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="block mb-0.5 text-sm text-gray-700">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block mb-0.5 text-sm text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div className="flex items-start">
              <input
                type="checkbox"
                name="terms_accepted"
                checked={formData.terms_accepted}
                onChange={handleChange}
                className="mt-0.5 h-3.5 w-3.5 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <label className="ml-2 text-xs text-gray-700 leading-snug">
                I agree to the&nbsp;
                <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                  Scucci Terms of Service, Privacy Policy, and Refund Policy
                </a>.
              </label>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-1.5 px-2 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full mt-1 text-sm text-blue-500 hover:underline"
            >
              Back to Home
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
